"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Region, FilterValue } from "@/shared/types/type/search";
import { useSearchUiStore } from "@/features/search/model/ui/store";
import { DirectionIcon, MapPinIcon, Typography } from "@/shared/ui/atoms";
import { CustomButton } from "@/shared/ui/molecules";
import { RegionModal } from "@/shared/ui/templates";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
interface RegionFilterButtonProps {
  regionList: Region[];
  hideRegionAllOption?: boolean;
  defaultRegionId?: number;
}
const RegionFilterButton = ({
  regionList,
  hideRegionAllOption,
  defaultRegionId
}: RegionFilterButtonProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const params = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const keyword = params?.get("query");
  const treatmentIds = params?.get("treatment");
  const {
    setRegionList,
    selectedSubRegions,
    setSelectedSubRegions
  } = useSearchUiStore();
  const [openRegionModal, setOpenRegionModal] = useState(() => {
    const modal = params?.get("modal");
    return modal === "region";
  });
  const defaultRegionText = t("commonSearch.filter.region");
  const hasRegion = selectedSubRegions.length > 0;
  const regionDisplayName = !hasRegion ? defaultRegionText : selectedSubRegions.length === 1 ? selectedSubRegions[0].name : `${selectedSubRegions[0].name} 외 ${selectedSubRegions.length - 1}`;
  const search = (regions: FilterValue[]) => {
    const regionIds = regions.map(item => item.id);
    const keywordQuery = keyword ? `?query=${keyword}` : "?";
    const regionQuery = regionIds.length > 0 ? `&region=${regionIds}` : "";
    const treatmentQuery = treatmentIds ? `&treatment=${treatmentIds}` : "";
    if (pathname?.includes("/search/ranking")) {
      return router.push(`/${locale}/search/ranking${keywordQuery}${regionQuery}${treatmentQuery}`);
    }
    if (pathname?.includes("/search/result")) {
      return router.push(`/${locale}/search/result${keywordQuery}${regionQuery}${treatmentQuery}`);
    }
    if (pathname?.includes("/search")) {
      return router.push(`/${locale}/search${keywordQuery}${regionQuery}${treatmentQuery}`);
    }
  };
  useEffect(() => {
    setRegionList(regionList);
    const region = params?.get("region");
    try {
      if (region || defaultRegionId != null) {
        const regionIds_0 = region ? region.split(",").map(id => parseInt(id)) : [defaultRegionId as number];
        const selectedSubRegions_0 = regionList.flatMap(region_0 => region_0.children).filter(child => regionIds_0.includes(child.id)) ?? [];
        setSelectedSubRegions(selectedSubRegions_0);
        return;
      }
      setSelectedSubRegions([]);
    } catch {}
  }, [defaultRegionId, regionList, params, setRegionList, setSelectedSubRegions]);
  return <>
      <CustomButton className={`rounded-full border flex items-center justify-between py-[7px] px-[14px] gap-1 ${hasRegion ? "border-primary-300 bg-primary-50" : "bg-white border-gray-200"}`} onClick={() => setOpenRegionModal(true)}>
        <MapPinIcon size={16} className={hasRegion ? "text-primary-600" : "text-gray-700"} />
        <Typography size={15} lineHeight={22} className={` ${hasRegion ? "text-primary-600 font-semibold" : "text-gray-700 font-medium"}`}>
          {regionDisplayName}
        </Typography>
        <DirectionIcon size={14} className={hasRegion ? "text-primary-600" : "text-gray-500"} />
      </CustomButton>
      <RegionModal key={openRegionModal ? "open" : "closed"} open={openRegionModal} onClose={() => setOpenRegionModal(false)} saveSelectedRegion={(regions_0?: FilterValue[]) => search(regions_0 ?? [])} hideRegionAllOption={hideRegionAllOption} />
    </>;
};
export default RegionFilterButton;